
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Phone, 
  Mail, 
  Building2, 
  Euro,
  Calendar,
  Users,
  FileText,
  Download,
  CheckCircle,
  TrendingUp
} from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { CRMLead } from '@/hooks/useCRMLeads';
import { useAuth } from '@/hooks/useAuth';

interface QuickscanLeadCardProps {
  lead: CRMLead;
  onDragStart: (e: React.DragEvent) => void;
  onClick: () => void;
  onExport: () => void;
  selectionMode?: boolean;
  selected?: boolean;
  onToggleSelection?: () => void;
}

export const QuickscanLeadCard: React.FC<QuickscanLeadCardProps> = ({ 
  lead, 
  onDragStart, 
  onClick, 
  onExport,
  selectionMode = false,
  selected = false,
  onToggleSelection
}) => {
  const { user } = useAuth();
  const isMyLead = user && lead.owner_id === user.id;
  
  const getPriorityColor = (score: number) => {
    if (score >= 80) return 'bg-green-100 border-green-300';
    if (score >= 60) return 'bg-yellow-100 border-yellow-300';
    return 'bg-red-100 border-red-300';
  };

  const isOverdue = lead.next_action_date && new Date(lead.next_action_date) < new Date();
  const hasQuickscanData = !!lead.quickscan_data;
  const hasHbeData = !!lead.hbe_data;
  const isHbeLead = lead.origin_type === 'hbe_calculator_submission';

  return (
    <Card
      className={`cursor-pointer hover:shadow-md transition-all duration-200 border-l-4 ${
        lead.score >= 70 
          ? 'border-l-green-500' 
          : 'border-l-yellow-500'
      } ${isOverdue ? 'ring-2 ring-red-200' : ''} ${
        selected ? 'ring-2 ring-primary bg-primary/5' : ''
      }`}
      draggable={!selectionMode}
      onDragStart={selectionMode ? undefined : onDragStart}
      onClick={onClick}
    >
      <CardContent className="p-4 space-y-3">
        {/* Company Info with checkbox */}
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-2 flex-1">
            {selectionMode && (
              <Checkbox 
                checked={selected} 
                onCheckedChange={onToggleSelection}
                onClick={(e) => e.stopPropagation()}
                className="mt-0.5"
              />
            )}
            <div className="flex-1">
              <h4 className="font-semibold text-sm text-gray-900 truncate">
                {lead.organization?.name || 
                 (isHbeLead && lead.hbe_data?.company_name ?
                   lead.hbe_data.company_name :
                   lead.quickscan_data?.company_name && lead.quickscan_data.company_name !== 'Quickscan Bedrijf' 
                   ? lead.quickscan_data.company_name
                   : lead.quickscan_data?.email 
                     ? `Quickscan: ${lead.quickscan_data.email.split('@')[0]}` 
                     : isHbeLead && lead.hbe_data?.email
                       ? `HBE: ${lead.hbe_data.email.split('@')[0]}`
                       : `${lead.quickscan_data?.business_type || 'Onbekend'} bedrijf`)}
              </h4>
              <p className="text-xs text-gray-500">
                {isHbeLead ? 
                  `${lead.hbe_data?.vehicles_count || 0} voertuigen` :
                  lead.organization?.sector || 
                 (lead.quickscan_data?.business_type ? 
                   lead.quickscan_data.business_type.charAt(0).toUpperCase() + lead.quickscan_data.business_type.slice(1) : 
                   'Onbekend')}
              </p>
            </div>
          </div>
          
          {/* Top Right Badges */}
          <div className="flex flex-col items-end gap-1">
            {/* HBE Calculator Badge */}
            {isHbeLead && hasHbeData && (
              <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700">
                🚗 HBE Calculator
              </Badge>
            )}

            {/* Quickscan Status Badge */}
            {!isHbeLead && hasQuickscanData && (
              <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700">
                <CheckCircle className="h-3 w-3 mr-1" />
                Quickscan
              </Badge>
            )}

            {/* Wants Proposal Badge for HBE */}
            {isHbeLead && lead.hbe_data?.wants_proposal && (
              <Badge variant="outline" className="text-xs bg-green-50 text-green-700">
                🎯 Wil voorstel
              </Badge>
            )}
            
            {/* Owner Badge */}
            {isMyLead && (
              <Badge variant="outline" className="text-xs bg-green-50 text-green-700">
                Mijn lead
              </Badge>
            )}
          </div>
        </div>

        {/* Key Metrics */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1">
              <Euro className="h-3 w-3 text-gray-400" />
              <span className="text-xs text-gray-600">
                {isHbeLead && lead.hbe_data ?
                  `€${Math.round(lead.hbe_data.total_voordeel).toLocaleString('nl-NL')} voordeel` :
                  hasQuickscanData && lead.quickscan_data?.monthly_energy_costs
                  ? `€${lead.quickscan_data.monthly_energy_costs.toLocaleString('nl-NL')}/mnd`
                  : 'Energiekosten onbekend'
                }
              </span>
            </div>
            <Badge variant="outline" className="text-xs">
              Score: {lead.score}%
            </Badge>
          </div>
          
          {/* HBE Metrics */}
          {isHbeLead && lead.hbe_data && (
            <>
              <div className="flex items-center gap-1">
                <Building2 className="h-3 w-3 text-gray-400" />
                <span className="text-xs text-gray-600">
                  {lead.hbe_data.vehicles_count} voertuig{lead.hbe_data.vehicles_count !== 1 ? 'en' : ''}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <TrendingUp className="h-3 w-3 text-gray-400" />
                <span className="text-xs text-gray-600">
                  {Math.round(lead.hbe_data.total_kwh).toLocaleString('nl-NL')} kWh
                </span>
              </div>
            </>
          )}

          {/* Quickscan Metrics */}
          {!isHbeLead && hasQuickscanData && lead.quickscan_data?.employee_count && (
            <div className="flex items-center gap-1">
              <Users className="h-3 w-3 text-gray-400" />
              <span className="text-xs text-gray-600">
                {lead.quickscan_data.employee_count} werknemers
              </span>
            </div>
          )}

          {!isHbeLead && hasQuickscanData && !lead.quickscan_data?.employee_count && lead.quickscan_data?.business_type && (
            <div className="flex items-center gap-1">
              <Building2 className="h-3 w-3 text-gray-400" />
              <span className="text-xs text-gray-600">
                {lead.quickscan_data.business_type.charAt(0).toUpperCase() + lead.quickscan_data.business_type.slice(1)}
              </span>
            </div>
          )}
        </div>

        {/* Contact Actions */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {(lead.contact?.email || lead.quickscan_data?.email || lead.hbe_data?.email) && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
                onClick={(e) => {
                  e.stopPropagation();
                  const email = lead.contact?.email || lead.quickscan_data?.email || lead.hbe_data?.email;
                  window.open(`mailto:${email}`, '_blank');
                }}
              >
                <Mail className="h-3 w-3" />
              </Button>
            )}
            {(lead.contact?.phone || lead.hbe_data?.phone) && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
                onClick={(e) => {
                  e.stopPropagation();
                  const phone = lead.contact?.phone || lead.hbe_data?.phone;
                  window.open(`tel:${phone}`, '_blank');
                }}
              >
                <Phone className="h-3 w-3" />
              </Button>
            )}
          </div>

          {/* Export Action */}
          {(hasQuickscanData || hasHbeData) && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={(e) => {
                e.stopPropagation();
                onExport();
              }}
            >
              <Download className="h-3 w-3" />
            </Button>
          )}
        </div>

        {/* Next Action */}
        {lead.next_action_date && (
          <div className={`text-xs p-2 rounded ${isOverdue ? 'bg-red-50 text-red-700' : 'bg-blue-50 text-blue-700'}`}>
            <div className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              <span>
                {format(new Date(lead.next_action_date), 'dd MMM', { locale: nl })}
              </span>
            </div>
          </div>
        )}

        {/* HBE Calculator Summary */}
        {isHbeLead && hasHbeData && (
          <div className="text-xs bg-purple-50 p-2 rounded border border-purple-100">
            <div className="flex items-center gap-1 mb-1">
              <FileText className="h-3 w-3 text-purple-500" />
              <span className="font-medium text-purple-900">HBE Calculator</span>
            </div>
            <div className="space-y-1 text-purple-900">
              <div className="flex items-center justify-between">
                <span>Voertuigen:</span>
                <span className="font-semibold">{lead.hbe_data.vehicles_count}</span>
              </div>
              <div className="flex items-center justify-between">
                <span>Totaal kWh:</span>
                <span className="font-semibold">{Math.round(lead.hbe_data.total_kwh).toLocaleString('nl-NL')}</span>
              </div>
              <div className="flex items-center justify-between">
                <span>Potentieel voordeel:</span>
                <span className="font-semibold text-green-600">€{Math.round(lead.hbe_data.total_voordeel).toLocaleString('nl-NL')}</span>
              </div>
            </div>
          </div>
        )}

        {/* Quickscan Summary */}
        {!isHbeLead && hasQuickscanData && (
          <div className="text-xs bg-gray-50 p-2 rounded">
            <div className="flex items-center gap-1 mb-1">
              <FileText className="h-3 w-3 text-gray-500" />
              <span className="font-medium">Quickscan Samenvatting</span>
            </div>
            <div className="space-y-1">
              {lead.quickscan_data?.energy_potential_score && (
                <div>Energiepotentie: {lead.quickscan_data.energy_potential_score}%</div>
              )}
              {lead.quickscan_data?.monthly_energy_costs && (
                <div>Maandelijkse kosten: €{lead.quickscan_data.monthly_energy_costs.toLocaleString('nl-NL')}</div>
              )}
              {lead.quickscan_data?.business_type && lead.quickscan_data?.employee_count && (
                <div>{lead.quickscan_data.business_type} • {lead.quickscan_data.employee_count} werknemers</div>
              )}
            </div>
          </div>
        )}


        {/* Creation Date */}
        <div className="text-xs text-gray-400 border-t pt-2">
          {format(new Date(lead.created_at), 'dd MMM yyyy', { locale: nl })}
        </div>
      </CardContent>
    </Card>
  );
};
